// First-load recovery runs before the gameplay UI or any account data is mounted.
// Only an explicit sign-out uses the existing account endpoint. A failed check
// never clears cookies, retries a mutation, or falls back to a guest identity.
export function createArrivalRecovery({loading,request,eventTarget=window,timeoutMs=18000,schedule=setTimeout,cancel=clearTimeout}={}) {
  const document=loading.ownerDocument,title=loading.querySelector('h2'),status=loading.querySelector('#loading-status'),line=loading.querySelector('.loading-line');
  const actions=document.createElement('div'),note=document.createElement('p'),retry=document.createElement('button'),signout=document.createElement('button');
  actions.className='arrival-actions';actions.hidden=true;
  note.id='arrival-recovery-note';note.hidden=true;
  retry.type=signout.type='button';retry.textContent='Retry connection';signout.textContent='Sign out and continue as guest';signout.hidden=true;
  for(const button of [retry,signout])button.setAttribute('aria-describedby',note.id);
  actions.append(retry,signout);loading.append(note,actions);
  let resolveLoad,rejectLoad,promise,active=null,generation=0,settled=false,disposed=false,paused=false,canSignout=false,expectGuest=false,logoutUncertain=false;
  const alive=id=>!disposed&&!settled&&!paused&&id===generation;
  function render({busy=false,message,detail,error=false}={}) {
    loading.toggleAttribute('data-arrival-error',error);
    title.textContent=error?'Arrival interrupted.':'Valoria';
    status.textContent=message;
    note.textContent=detail??'';note.hidden=!detail;
    actions.hidden=!error&&!busy;retry.disabled=signout.disabled=busy;
    signout.hidden=!canSignout||expectGuest;
    retry.textContent=busy?'Connecting…':'Retry connection';
    line.hidden=error;
    actions.setAttribute('aria-busy',String(busy));
  }
  function failed(error,{logout=false}={}) {
    if(error.code==='ACCOUNT_UNAVAILABLE')canSignout=true;
    const account=canSignout&&!expectGuest;
    render({error:true,
      message:logout?'We couldn’t confirm sign-out.':error.code==='ACCOUNT_CHANGED'?'Your sign-in changed while you were arriving.':account?'Account sign-in is temporarily unavailable.':'We couldn’t connect to Valoria.',
      detail:logout?'Retry sign-out, or retry the connection to check your current session. Nothing from your account has been loaded here.':expectGuest?'Retry to enter as a guest. If you signed in again in another tab, reload this page to use that account.':account?'Retry to keep using your account, or sign out to enter as a guest. Your saved account profile and worlds stay on your account; you’ll need to sign in again to use them.':'Check your connection, then try again. Any unsent text in this tab is unchanged.'});
  }
  async function bounded(path,body,method) {
    const controller=new AbortController();let timer;
    active=controller;
    try {
      const timeout=new Promise((_,reject)=>{timer=schedule(()=>{controller.abort();reject(Object.assign(Error('Connection timed out'),{code:'ARRIVAL_TIMEOUT'}));},timeoutMs);});
      const aborted=new Promise((_,reject)=>controller.signal.addEventListener('abort',()=>reject(Object.assign(Error('Arrival was interrupted'),{code:'ARRIVAL_INTERRUPTED'})),{once:true}));
      return await Promise.race([request(path,body,method,{signal:controller.signal}),timeout,aborted]);
    }finally{cancel(timer);if(active===controller)active=null;}
  }
  async function attempt(logout=false) {
    if(disposed||settled||paused||active)return;
    const id=++generation;
    render({busy:true,message:logout?'Signing out…':'Checking your connection…',detail:logout?'Your saved account profile and worlds will stay on your account.':canSignout&&!expectGuest?'Your account session is kept while we retry.':undefined});
    try {
      if(logout){
        logoutUncertain=true;
        const result=await bounded('/api/auth/logout',{},'POST');
        if(!alive(id))return;
        if(!result||!Object.hasOwn(result,'user')||result.user!==null)throw Error('Sign-out was not confirmed');
        expectGuest=true;canSignout=false;logoutUncertain=false;
        render({busy:true,message:'Entering as a guest…'});
      }
      const data=await bounded('/api/bootstrap',undefined,'GET');
      if(!alive(id))return;
      if(expectGuest&&data?.account?.kind!=='guest')throw Object.assign(Error('Guest session was not confirmed'),{code:'ACCOUNT_CHANGED'});
      if(!data?.player||!data?.world)throw Error('Arrival response was incomplete');
      settled=true;removeListeners();actions.remove();note.remove();loading.removeAttribute('data-arrival-error');line.hidden=false;title.textContent='Valoria';status.textContent='Preparing your arrival';
      resolveLoad(data);
    }catch(error){
      if(!alive(id))return;
      if(error.code==='SIGN_IN_REQUIRED'){settled=true;removeListeners();actions.remove();note.remove();rejectLoad(error);return;}
      failed(error,{logout:logoutUncertain});
    }
  }
  function pagehide(){
    if(settled||disposed)return;
    paused=true;generation++;active?.abort();active=null;
  }
  function pageshow(){
    if(!paused||settled||disposed)return;
    paused=false;
    failed(Object.assign(Error('Arrival was interrupted'),{code:canSignout?'ACCOUNT_UNAVAILABLE':'ARRIVAL_INTERRUPTED'}),{logout:logoutUncertain});
  }
  function removeListeners(){eventTarget.removeEventListener('pagehide',pagehide);eventTarget.removeEventListener('pageshow',pageshow);}
  retry.onclick=()=>attempt();signout.onclick=()=>{if(canSignout&&!expectGuest)return attempt(true);};
  eventTarget.addEventListener('pagehide',pagehide);eventTarget.addEventListener('pageshow',pageshow);
  return {
    load(){if(!promise){promise=new Promise((yes,no)=>{resolveLoad=yes;rejectLoad=no;});void attempt();}return promise;},
    dispose(){if(disposed)return;disposed=true;generation++;active?.abort();active=null;removeListeners();actions.remove();note.remove();},
  };
}
